import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, AlertCircle, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL_MS = 2000;

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | expired | error | timeout
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setInfo(res.data);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.data.status === "expired") {
          setState("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6" data-testid="booking-success-page">
      <div className="bg-bone-white border border-deep-forest/10 p-12 max-w-lg w-full text-center">
        {state === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-terracotta animate-spin mx-auto" />
            <div className="font-serif text-3xl text-deep-forest mt-6">Confirming your payment…</div>
            <div className="text-sm text-deep-forest/60 mt-3">Hold on a sec — we&apos;re finalising the booking with our payment provider.</div>
          </>
        )}
        {state === "paid" && (
          <div data-testid="booking-success-paid">
            <div className="mx-auto w-14 h-14 border border-deep-forest/20 flex items-center justify-center">
              <Check className="w-6 h-6 text-terracotta" />
            </div>
            <div className="font-serif text-3xl text-deep-forest mt-6">Booking confirmed</div>
            <div className="text-sm text-deep-forest/60 mt-3">
              We&apos;ve received your payment of <strong>${info?.amount_total?.toFixed(2)} AUD</strong>. You&apos;ll get a confirmation by email shortly.
            </div>
            <button onClick={() => navigate("/dashboard")} data-testid="booking-success-dashboard-btn" className="btn-primary mt-8">Go to my dashboard</button>
          </div>
        )}
        {state === "expired" && (
          <>
            <AlertCircle className="w-10 h-10 text-terracotta mx-auto" />
            <div className="font-serif text-3xl text-deep-forest mt-6">Payment expired</div>
            <div className="text-sm text-deep-forest/60 mt-3">Your checkout session timed out. Please start a new booking.</div>
            <button onClick={() => navigate("/book")} className="btn-primary mt-8">Try again</button>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="w-10 h-10 text-deep-forest/60 mx-auto" />
            <div className="font-serif text-3xl text-deep-forest mt-6">Still processing</div>
            <div className="text-sm text-deep-forest/60 mt-3">This is taking a moment. Your booking will show up on your dashboard once payment clears.</div>
            <button onClick={() => navigate("/dashboard")} className="btn-primary mt-8">Go to dashboard</button>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="w-10 h-10 text-destructive mx-auto" />
            <div className="font-serif text-3xl text-deep-forest mt-6">Something went wrong</div>
            <div className="text-sm text-deep-forest/60 mt-3">We couldn&apos;t verify the payment. If you were charged, the booking will appear on your dashboard automatically.</div>
            <button onClick={() => navigate("/dashboard")} className="btn-primary mt-8">Open dashboard</button>
          </>
        )}
      </div>
    </div>
  );
}
